import CryptoJS from 'crypto-js';

export class EncryptionError extends Error {
  constructor(message) {
    super(message);
    this.name = 'EncryptionError';
  }
}

export class DecryptionError extends Error {
  constructor(message) {
    super(message);
    this.name = 'DecryptionError';
  }
}

const getEncryptionKey = () => {
  const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY || '';
  
  if (!encryptionKey) {
    throw new EncryptionError('ENCRYPTION_KEY not configured');
  }
  
  return encryptionKey;
};

export const encryptData = (data) => {
  try {
    const fernetKey = getEncryptionKey();
    
    const keyBuffer = CryptoJS.enc.Base64.parse(fernetKey);
    const signingKey = CryptoJS.lib.WordArray.create(keyBuffer.words.slice(0, 4), 16);
    const encryptionKey = CryptoJS.lib.WordArray.create(keyBuffer.words.slice(4, 8), 16);
    
    const version = CryptoJS.lib.WordArray.create([0x80000000], 1);
    
    const timestamp = Math.floor(Date.now() / 1000);
    const timestampHigh = Math.floor(timestamp / 0x100000000);
    const timestampLow = timestamp >>> 0;
    const timestampBytes = CryptoJS.lib.WordArray.create([timestampHigh, timestampLow], 8);
    
    const iv = CryptoJS.lib.WordArray.random(16);
    
    const encrypted = CryptoJS.AES.encrypt(data, encryptionKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    
    const tokenWithoutHmac = version.clone()
      .concat(timestampBytes)
      .concat(iv)
      .concat(encrypted.ciphertext);
    
    const hmac = CryptoJS.HmacSHA256(tokenWithoutHmac, signingKey);
    const finalToken = tokenWithoutHmac.concat(hmac);
    
    return CryptoJS.enc.Base64.stringify(finalToken);
    
  } catch (error) {
    throw new EncryptionError(`Failed to encrypt data: ${error.message}`);
  }
};

// Helper function to convert CryptoJS WordArray to Uint8Array
const wordArrayToByteArray = (wordArray) => {
  const bytes = new Uint8Array(wordArray.sigBytes);
  for (let i = 0; i < wordArray.sigBytes; i++) {
    const wordIdx = Math.floor(i / 4);
    const byteIdx = i % 4;
    bytes[i] = (wordArray.words[wordIdx] >>> (24 - byteIdx * 8)) & 0xff;
  }
  return bytes;
};

// Helper function to convert byte array to CryptoJS WordArray
const byteArrayToWordArray = (bytes) => {
  const words = [];
  for (let i = 0; i < bytes.length; i += 4) {
    words.push(
      (bytes[i] << 24) | 
      ((bytes[i + 1] || 0) << 16) | 
      ((bytes[i + 2] || 0) << 8) | 
      (bytes[i + 3] || 0)
    );
  }
  return CryptoJS.lib.WordArray.create(words, bytes.length);
};

// Browser-compatible Fernet decryption using CryptoJS (same format as Admin encryptionUtils)
export const decryptDataWithFernet = (encryptedData) => {
  try {
    const fernetKey = getEncryptionKey();
    
    // Parse the Fernet token (Fernet uses URL-safe base64)
    // Convert URL-safe base64 to standard base64
    const standardBase64Token = encryptedData.replace(/-/g, '+').replace(/_/g, '/');
    const token = CryptoJS.enc.Base64.parse(standardBase64Token);
    
    // Parse the 32-byte Fernet key (also URL-safe base64)
    const standardBase64Key = fernetKey.replace(/-/g, '+').replace(/_/g, '/');
    const keyBuffer = CryptoJS.enc.Base64.parse(standardBase64Key);
    
    if (keyBuffer.sigBytes !== 32) {
      throw new Error(`Invalid Fernet key length: ${keyBuffer.sigBytes} bytes, expected 32`);
    }
    
    const signingKey = CryptoJS.lib.WordArray.create(keyBuffer.words.slice(0, 4), 16);
    const encryptionKey = CryptoJS.lib.WordArray.create(keyBuffer.words.slice(4, 8), 16);
    
    const tokenLength = token.sigBytes;
    
    if (tokenLength < 57) { // 1 + 8 + 16 + 0 + 32 = minimum 57 bytes
      throw new Error(`Invalid Fernet token length: ${tokenLength} bytes`);
    }
    
    // Extract version (should be 0x80)
    const version = (token.words[0] >>> 24) & 0xFF;
    if (version !== 0x80) {
      throw new Error(`Invalid Fernet version: 0x${version.toString(16)}, expected 0x80`);
    }
    
    const hmacStart = tokenLength - 32;
    
    // Convert token to bytes first for proper HMAC extraction
    const allTokenBytes = wordArrayToByteArray(token);
    
    // Extract token without HMAC (first hmacStart bytes)
    const tokenWithoutHmacBytes = allTokenBytes.slice(0, hmacStart);
    const tokenWithoutHmac = byteArrayToWordArray(tokenWithoutHmacBytes);
    
    // Extract received HMAC (last 32 bytes)
    const receivedHmacBytes = allTokenBytes.slice(hmacStart);
    const receivedHmac = byteArrayToWordArray(receivedHmacBytes);
    
    // Verify HMAC
    const calculatedHmac = CryptoJS.HmacSHA256(tokenWithoutHmac, signingKey);
    
    if (calculatedHmac.toString() !== receivedHmac.toString()) {
      throw new Error('Fernet HMAC verification failed');
    }
    
    // Extract IV (bytes 9-24)
    const ivStart = 9;
    const ivBytes = allTokenBytes.slice(ivStart, ivStart + 16);
    const iv = byteArrayToWordArray(ivBytes);
    
    // Extract ciphertext (bytes 25 to hmacStart)
    const ciphertextStart = 25;
    const ciphertextBytes = allTokenBytes.slice(ciphertextStart, hmacStart);
    const ciphertext = byteArrayToWordArray(ciphertextBytes);
    
    // Decrypt using AES-128-CBC
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext },
      encryptionKey,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );
    
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      throw new Error('Fernet decryption resulted in empty string');
    }
    
    return decryptedString;
    
  } catch (error) {
    throw new DecryptionError(`Failed to decrypt data: ${error.message}`);
  }
};

/** @deprecated Use decryptDataWithFernet */
export const decryptData = decryptDataWithFernet;

export const encryptJSON = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    return encryptData(jsonString);
  } catch (error) {
    throw new EncryptionError(`Failed to encrypt JSON data: ${error.message}`);
  }
};

export const decryptJSON = (encryptedData) => {
  try {
    const decryptedString = decryptDataWithFernet(encryptedData);
    return JSON.parse(decryptedString);
  } catch (error) {
    if (error instanceof DecryptionError) {
      throw error;
    }
    throw new DecryptionError(`Failed to decrypt JSON data: ${error.message}`);
  }
};

// Response decryption helpers (same format as Admin encryptionUtils)
export const isEncryptedResponse = (response) => {
  if (!response || typeof response !== 'object') return false;
  // Axios response: body is raw encrypted string
  if (typeof response.data === 'string') return true;
  // Axios response: body is { data: "encrypted_string" }
  if (
    response.data &&
    typeof response.data === 'object' &&
    typeof response.data.data === 'string'
  ) return true;
  return false;
};

export const decryptResponse = (response) => {
  try {
    let encryptedData;
    if (typeof response.data === 'string') {
      encryptedData = response.data;
    } else if (response.data && typeof response.data.data === 'string') {
      encryptedData = response.data.data;
    } else {
      throw new Error('Could not find encrypted data in response structure');
    }
    return decryptJSON(encryptedData);
  } catch (error) {
    throw new DecryptionError(`Failed to decrypt API response: ${error.message}`);
  }
};
